/**
 * Tabs, with the keyboard behaviour a row of buttons does not have.
 *
 * Arrow keys move between tabs, the roving tabindex means one Tab keypress
 * skips the whole strip, and the current tab is `aria-selected` rather than a
 * class somebody has to remember to test. The strip is the whole component:
 * callers render the panel for the selected value themselves, because most
 * screens here already hold the value in the route.
 */

import * as TabsPrimitive from '@radix-ui/react-tabs';
import type React from 'react';

export interface TabItem {
  value: string;
  label: React.ReactNode;
  /** A count beside the label. Zero renders nothing. */
  badge?: number;
  disabled?: boolean;
}

export function Tabs({
  value,
  onChange,
  items,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  items: readonly TabItem[];
  /** The accessible name of the strip: "Asset detail sections". */
  label: string;
}): React.JSX.Element {
  return (
    <TabsPrimitive.Root value={value} onValueChange={onChange}>
      <TabsPrimitive.List className="tabs" aria-label={label}>
        {items.map((item) => (
          <TabsPrimitive.Trigger
            key={item.value}
            value={item.value}
            className="tab"
            disabled={item.disabled === true}
            /*
             * Radix points each tab at a panel it expects to render. Callers
             * render their own panel here, so that id names nothing and axe
             * reports it as a critical ARIA error on every screen with tabs.
             * The attribute is optional; an absent one is correct, a dangling
             * one is not.
             */
            aria-controls={undefined}
          >
            {item.label}
            {item.badge !== undefined && item.badge > 0 && (
              <span className="pill count">{item.badge}</span>
            )}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
    </TabsPrimitive.Root>
  );
}
